/* MUI */
import { Typography } from '@mui/material';

/*  React  */
import { CellContext } from '@tanstack/react-table';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ColumnDef } from '../Table/types';

/**
 * A React component that renders a string value in a table cell.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.column - The column object.
 * @param {Object} props.column.columnDef - The column definition object.
 * @param {string} props.column.columnDef.accessorKey - The key to access the value in the row object.
 * @param {string} [props.column.columnDef.defaultKey] - The translation key for the default value.
 * @param {Object} props.cell - The cell object containing the data.
 *
 * @returns {React.ReactElement} The rendered table cell component.
 */
export const StringCell = <TData,>({
    cell,
    column
}: CellContext<TData, unknown>): React.ReactElement => {
    const { t } = useTranslation();

    const value = cell.getValue() as string;
    const columnDef = column.columnDef as ColumnDef<TData, string>;
    const defaultValue = t(columnDef.defaultKey || 'Common.none');
    return (
        <Typography
            // Showing the ellipsis will only work on tables where we have set up a maxSize
            // By default, tanstack sets the maxSize to the MAX_SAFE_INTEGER
            {...(column.columnDef.maxSize !== Number.MAX_SAFE_INTEGER
                ? {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      title: value,
                      whiteSpace: 'nowrap'
                  }
                : {})}
        >
            {value || defaultValue}
        </Typography>
    );
};
